import { useEffect, useState } from 'react';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { useAuthStore } from '@/store/auth';
import { coachService } from '@/services/coach';
import type { Coach } from '@court-sync/shared';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { Button } from '@/components/ui';
import { PhoneInput, PhoneNumberLabel } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { AvatarUpload } from '@/components/AvatarUpload';
import { uploadUserAvatar } from '@/services/storage';

export default function CoachProfilePage() {
    const { academyId } = useCoachAcademyStore();
    const { user, updateUser } = useAuthStore();
    const [coach, setCoach] = useState<Coach | null>(null);
    const [form, setForm] = useState({ bio: '', phone: '', specialties: '' });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!academyId) return;
        coachService.getCoachSchedule(academyId).then((data) => {
            setCoach(data.coach);
            setForm({
                bio: data.coach.bio ?? '',
                phone: data.coach.phone ?? '',
                specialties: data.coach.specialties?.join(', ') ?? '',
            });
        }).catch(console.error);
    }, [academyId]);

    const handleSave = async () => {
        if (!coach || !academyId) return;
        try {
            setSaving(true);
            setError(null);
            const specialtiesArr = form.specialties
                ? form.specialties.split(',').map((s) => s.trim()).filter(Boolean)
                : [];
            await coachService.updateMyProfile(academyId, coach.id, {
                bio: form.bio,
                phone: form.phone,
                specialties: specialtiesArr,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="contents">
            <PageHeader
                title="My Profile"
                help="Update your coaching profile information"
            />

            {error && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">{error}</div>
            )}

            <div className="max-w-2xl space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        {user ? (
                            <AvatarUpload
                                avatarUrl={user.avatarUrl}
                                firstName={user.firstName}
                                lastName={user.lastName}
                                onUpload={async (file) => uploadUserAvatar(file, user.uid)}
                                onSave={async (avatarUrl) => updateUser({ avatarUrl })}
                                onRemove={async () => updateUser({ avatarUrl: null })}
                                size="md"
                            />
                        ) : (
                            <div className="h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center" />
                        )}
                        <div>
                            <h2 className="font-semibold text-gray-900 dark:text-white">
                                {coach ? `${coach.firstName} ${coach.lastName}` : 'Loading...'}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{coach?.email}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <PhoneNumberLabel htmlFor="coach-profile-phone" purpose="coach_contact" className="mb-1">
                            Phone
                        </PhoneNumberLabel>
                        <PhoneInput
                            id="coach-profile-phone"
                            value={form.phone}
                            onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
                            placeholder="Phone number"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Specialties
                        </label>
                        <input
                            type="text"
                            value={form.specialties}
                            onChange={(e) => setForm((prev) => ({ ...prev, specialties: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. Serve Technique, Beginner Kids, Fitness"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Comma-separated list</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bio
                        </label>
                        <textarea
                            value={form.bio}
                            onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))}
                            rows={4}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Tell students about your coaching background..."
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={handleSave} disabled={saving}>
                            {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
            </div>
        </div>
    );
}
